import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SystemOptionValue } from '@api/types';
import type { IOption } from '@shared/form/types';

import {
  useGetCountriesQuery,
  useGetCurrenciesQuery,
  useGetEnumsQuery,
  useGetLocalesQuery,
  useGetTimeZonesQuery,
} from '@api/systemValuesApiSlice';
import { usePageVisibilityState } from '@hooks/usePageVisibilityState';

type Value = number | string;

export interface IGetOptions {
  key: SystemOptionValue;
  withDisabled?: boolean;
  allowedValues?: Value[];
  disabledValues?: Value[];
  restrictedValues?: Value[];
}

export interface IGetLabel {
  key: SystemOptionValue;
  value: number | string;
}

interface IUseCatalog {
  isLoading: boolean;
  localeOptions: IOption[];
  countryOptions: IOption[];
  currencyOptions: IOption[];
  timeZoneOptions: IOption[];
  getLabel: (data: IGetLabel) => string;
  getOptions: (data: IGetOptions) => IOption[];
}

const POLLING_INTERVAL = 300_000;

export const useCatalog = (): IUseCatalog => {
  const isVisible = usePageVisibilityState();

  const pollingInterval = isVisible ? POLLING_INTERVAL : 0;

  const { data: catalogEnums, isLoading: isLoadingEnums } = useGetEnumsQuery(
    '',
    { pollingInterval }
  );
  const { data: timeZoneOptions = [], isLoading: isLoadingTimeZones } =
    useGetTimeZonesQuery('', { pollingInterval });
  const { data: countryOptions = [], isLoading: isLoadingCountries } =
    useGetCountriesQuery('', { pollingInterval });
  const { data: currencyOptions = [], isLoading: isLoadingCurrencies } =
    useGetCurrenciesQuery('', { pollingInterval });
  const { data: localeOptions = [], isLoading: isLoadingLocales } =
    useGetLocalesQuery('', { pollingInterval });

  const { t } = useTranslation('serverTranslation');

  const translateOptions = useCallback(
    <T extends IOption | IOption[]>(data: T): T => {
      if (Array.isArray(data)) {
        return data.map(({ label, translationKey, ...option }) => ({
          ...option,
          label: translationKey ? t(translationKey) : label,
        })) as T;
      }

      return {
        ...data,
        label: data.translationKey ? t(data.translationKey) : data.label,
      };
    },
    [t]
  );

  const getOptions = useCallback(
    ({
      key,
      allowedValues,
      disabledValues,
      restrictedValues,
      withDisabled = true,
    }: IGetOptions): IOption[] => {
      if (!catalogEnums) {
        return [];
      }

      const rawOptions = translateOptions(catalogEnums[key] ?? []);

      const options = restrictedValues?.length
        ? rawOptions.filter(
            (option) => !restrictedValues.includes(option.value)
          )
        : rawOptions;

      if (allowedValues?.length) {
        return options
          .map((option) => ({
            ...option,
            disabled: withDisabled && !allowedValues.includes(option.value),
          }))
          .filter(
            (option) => withDisabled || allowedValues.includes(option.value)
          );
      }

      if (disabledValues?.length) {
        return options
          .map((option) => ({
            ...option,
            disabled: disabledValues.includes(option.value),
          }))
          .filter((option) => !option.disabled || withDisabled);
      }

      return options;
    },
    [catalogEnums, translateOptions]
  );

  const getLabel = useCallback(
    ({ key, value }: IGetLabel): string => {
      if (!catalogEnums) {
        return '';
      }

      const option = catalogEnums[key]?.find(
        (option) => option.value === value
      );

      return option ? translateOptions(option).label : '';
    },
    [catalogEnums, translateOptions]
  );

  const isLoading = useMemo(
    () =>
      isLoadingEnums ||
      isLoadingTimeZones ||
      isLoadingCountries ||
      isLoadingCurrencies ||
      isLoadingLocales,
    [
      isLoadingEnums,
      isLoadingTimeZones,
      isLoadingCountries,
      isLoadingCurrencies,
      isLoadingLocales,
    ]
  );

  return {
    getLabel,
    isLoading,
    getOptions,
    localeOptions,
    countryOptions,
    timeZoneOptions,
    currencyOptions,
  };
};
